import { createBrowserRouter, Navigate } from 'react-router-dom';
import { DashboardPage } from '@app/pages/dashboard/DashboardPage';
import { HomePage } from '@app/pages/home/HomePage';
import { SucursalVirtualPage } from '@app/pages/sucursal-virtual/SucursalVirtualPage';
import { PlanesyCoberturasPage } from '@app/pages/planes-y-coberturas/PlanesyCoberturasPage';
import { PlanDetailPage } from '@app/pages/planes-y-coberturas/PlanDetailPage';
import { ContratacionPage } from '@app/pages/contratacion/ContratacionPage';
import { LoginPage } from '@app/pages/login/LoginPage';
import { RegisterPage } from '@app/pages/register/RegisterPage';
import { NotFoundPage } from '@app/pages/NotFoundPage';
import { ChatPage } from '@app/pages/chat/ChatPage';
import { NotificacionesPage } from '@app/pages/notificaciones/NotificacionesPage';
import { AdminDashboardPage } from '@app/pages/admin/AdminDashboardPage';
import AdminPlansPage from '@app/pages/admin/AdminPlansPage';
import { AdminUsersPage } from '@app/pages/admin/AdminUsersPage';
import { AdminCouponsPage } from '@app/pages/admin/AdminCouponsPage';
import { AdminClaimsPage } from '@app/pages/admin/AdminClaimsPage';
import { AdminEnterprisesPage } from '@app/pages/admin/AdminEnterprisesPage';
import { AdminContractsPage } from '@app/pages/admin/AdminContractsPage';
import { AdminEnterpriseInvoicesPage } from '@app/pages/admin/AdminEnterpriseInvoicesPage';
import { AdminPaymentsPage } from '@app/pages/admin/AdminPaymentsPage';
import { AdminBenefitsPage } from '@app/pages/admin/AdminBenefitsPage';
import { AdminVeterinariesPage } from '@app/pages/admin/AdminVeterinariesPage';
import { AdminBankAccountsPage } from '@app/pages/admin/AdminBankAccountsPage';
import { AdminPricingRulesPage } from '@app/pages/admin/AdminPricingRulesPage';
import { AdminIndicatorsPage } from '@app/pages/admin/AdminIndicatorsPage';
import { AdminAssetsPage } from '@app/pages/admin/AdminAssetsPage';
import { MascotasPage } from '@app/pages/mascotas/MascotasPage';
import { MascotaDetailPage } from '@app/pages/mascotas/MascotaDetailPage';
import { BonificacionesPage } from '@app/pages/bonificaciones/BonificacionesPage';
import { NuevaBonificacionPage } from '@app/pages/bonificaciones/NuevaBonificacionPage';
import { DetalleBonificacionPage } from '@app/pages/bonificaciones/DetalleBonificacionPage';
import EditarPlanPage from '@app/pages/admin/EditarPlanPage';
import { CrearPlanPage } from '@app/pages/admin/CrearPlanPage';
import PlanPDFEditorPage from '@app/pages/admin/PlanPDFEditorPage';
import { AdminPlanPageConfigPage } from '@app/pages/admin/AdminPlanPageConfigPage';
import { PaymentPage } from '@app/pages/pagos/PaymentPage';
import { PaymentSuccessPage } from '@app/pages/pagos/PaymentSuccessPage';
import { TransbankCallbackPage } from '@app/pages/pagos/TransbankCallbackPage';
import { SuscripcionesPage } from '@app/pages/suscripciones/SuscripcionesPage';
import { BlogPage } from '@app/pages/blog/BlogPage';
import { ReferralsPage } from '@app/pages/invita-y-gana/ReferralsPage';
import { BenefitsPage as PortalBenefitsPage } from '@app/pages/beneficios/BenefitsPage';
import { OptionsPage } from '@app/pages/opciones/OptionsPage';
import { PlanDetailPortalPage } from '@app/pages/planes-y-coberturas/PlanDetailPortalPage';
import { ProtectedRoute } from '@app/components/ProtectedRoute';
import { AdminProtectedRoute } from '@app/components/AdminProtectedRoute';
import { AdminLayout } from '@app/components/AdminLayout';
import { PortalLayout } from '@app/components/PortalLayout';
import { ErrorBoundary } from '@app/components/ErrorBoundary';

/**
 * CONVENCIONES DE RUTAS:
 * - RUTAS FRONTEND: En ESPAÑOL (/planes, /mascotas, /bonificaciones, /mi-cuenta)
 * - RUTAS BACKEND API: En INGLÉS (/api/v1/plans, /api/v1/pets, /api/v1/claims)
 *
 * Estructura:
 * 1. Públicas: landing, login, registro, planes públicos.
 * 2. Portal de cliente: bajo <PortalLayout /> con sidebar compartido.
 * 3. Pantallas full-screen autenticadas (callbacks de pago) sin layout.
 * 4. Admin: bajo <AdminLayout /> (sin tocar).
 */

export const router = createBrowserRouter([
  // ─── Públicas ───────────────────────────────────────────────────────
  {
    path: '/',
    element: <Navigate to="/inicio" replace />,
    errorElement: <ErrorBoundary />,
  },
  { path: '/inicio', element: <HomePage /> },
  { path: '/iniciar-sesion', element: <LoginPage /> },
  { path: '/registro', element: <RegisterPage /> },
  { path: '/planes', element: <PlanesyCoberturasPage /> },
  { path: '/planes/:planId', element: <PlanDetailPage /> },
  { path: '/planes-y-coberturas', element: <PlanesyCoberturasPage /> },

  // ─── Portal de cliente (PortalLayout) ───────────────────────────────
  {
    element: (
      <ProtectedRoute>
        <PortalLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/tablero', element: <DashboardPage /> },
      { path: '/mi-cuenta', element: <SucursalVirtualPage /> },
      { path: '/sucursal-virtual', element: <SucursalVirtualPage /> },
      { path: '/mascotas', element: <MascotasPage /> },
      { path: '/mascotas/:id', element: <MascotaDetailPage /> },
      { path: '/bonificaciones', element: <BonificacionesPage /> },
      { path: '/bonificaciones/nueva', element: <NuevaBonificacionPage /> },
      { path: '/bonificaciones/:id', element: <DetalleBonificacionPage /> },
      { path: '/chat', element: <ChatPage /> },
      { path: '/notificaciones', element: <NotificacionesPage /> },
      { path: '/suscripciones', element: <SuscripcionesPage /> },
      { path: '/pagos', element: <PaymentPage /> },
      { path: '/contratacion', element: <ContratacionPage /> },
      { path: '/mi-cuenta/planes/:planId', element: <PlanDetailPortalPage /> },
      // Portal cliente (páginas reales)
      { path: '/blog', element: <BlogPage /> },
      { path: '/invita-y-gana', element: <ReferralsPage /> },
      { path: '/beneficios', element: <PortalBenefitsPage /> },
      { path: '/opciones', element: <OptionsPage /> },
    ],
  },

  // ─── Full-screen autenticadas (callbacks de pago) ───────────────────
  {
    path: '/pagos/exito',
    element: (
      <ProtectedRoute>
        <PaymentSuccessPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/billing/transbank/callback',
    element: (
      <ProtectedRoute>
        <TransbankCallbackPage />
      </ProtectedRoute>
    ),
  },

  // ─── Admin ──────────────────────────────────────────────────────────
  {
    path: '/admin',
    element: (
      <AdminProtectedRoute>
        <AdminLayout />
      </AdminProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: 'planes', element: <AdminPlansPage /> },
      { path: 'planes/nuevo', element: <CrearPlanPage /> },
      { path: 'planes/:id/editar', element: <EditarPlanPage /> },
      { path: 'planes/:id/pdf', element: <PlanPDFEditorPage /> },
      { path: 'planes/:id/pagina', element: <AdminPlanPageConfigPage /> },
      { path: 'usuarios', element: <AdminUsersPage /> },
      { path: 'cupones', element: <AdminCouponsPage /> },
      { path: 'bonificaciones', element: <AdminClaimsPage /> },
      { path: 'empresas', element: <AdminEnterprisesPage /> },
      { path: 'contratos', element: <AdminContractsPage /> },
      { path: 'facturas-empresariales', element: <AdminEnterpriseInvoicesPage /> },
      { path: 'pagos', element: <AdminPaymentsPage /> },
      { path: 'beneficios', element: <AdminBenefitsPage /> },
      { path: 'veterinarias', element: <AdminVeterinariesPage /> },
      { path: 'cuentas-bancarias', element: <AdminBankAccountsPage /> },
      { path: 'reglas-precio', element: <AdminPricingRulesPage /> },
      { path: 'indicadores', element: <AdminIndicatorsPage /> },
      { path: 'archivos', element: <AdminAssetsPage /> },
    ],
  },

  // ─── 404 ────────────────────────────────────────────────────────────
  {
    path: '*',
    element: <NotFoundPage />,
    errorElement: <ErrorBoundary />,
  },
]);
